'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import {
  HeroSection,
  FaqSection,
  PincodeCtaBannerSection,
  GetDirectionsSection,
} from 'components/performance-lab';
import {
  GLP_HERO_BACKGROUND,
  GLP_MARQUEE_PREFIX,
  GLP_MARQUEE_SIGNALS,
  GLP_FAQ_ITEMS,
} from 'components/performance-lab/glp';
import { useGlpSignUp } from 'components/performance-lab/glp/useGlpSignUp';
import { useScrollToPerformanceLabSection } from 'components/performance-lab/hooks';

const TextRevealSection = dynamic(
  () =>
    import('components/performance-lab/TextRevealSection').then(
      (mod) => mod.TextRevealSection,
    ),
  { ssr: false },
);
const GlpLongevityProtocol = dynamic(
  () =>
    import('components/performance-lab/glp').then(
      (mod) => mod.GlpLongevityProtocol,
    ),
  { ssr: false },
);
const GlpGuidedProtocol = dynamic(
  () =>
    import('components/performance-lab/glp').then(
      (mod) => mod.GlpGuidedProtocol,
    ),
  { ssr: false },
);
const GlpKnowYourProgram = dynamic(
  () =>
    import('components/performance-lab/glp').then(
      (mod) => mod.GlpKnowYourProgram,
    ),
  { ssr: false },
);
const GlpJourneyQuestions = dynamic(
  () =>
    import('components/performance-lab/glp').then(
      (mod) => mod.GlpJourneyQuestions,
    ),
  { ssr: false },
);
const GlpScienceCards = dynamic(
  () =>
    import('components/performance-lab/glp').then((mod) => mod.GlpScienceCards),
  { ssr: false },
);

const PerformanceLabGlpPage = () => {
  const t = useTranslations('performance-lab');
  const handleGlpSignUp = useGlpSignUp();
  const scrollToProgramSection = useScrollToPerformanceLabSection(
    'glp-program-heading',
  );

  const glpFaqData = GLP_FAQ_ITEMS.map((item) => ({
    title: t(item.questionKey),
    description: t(item.answerKey),
  }));

  return (
    <>
      <HeroSection
        ariaLabel={t('glp.hero.aria')}
        locationLabel={t('glp.hero.eyebrow')}
        title={t('glp.hero.title')}
        subtitle=""
        align="center"
        showStats={false}
        titleMobileFontSize={40}
        overlayColor="rgba(0, 0, 0, 0.24)"
        background={{
          type: 'image',
          desktop: GLP_HERO_BACKGROUND.desktop,
          mobile: GLP_HERO_BACKGROUND.mobile,
        }}
        cta={{
          label: t('glp.hero.cta'),
          ariaLabel: t('glp.hero.ctaAria'),
          onClick: scrollToProgramSection,
          buttontype: 'performance_lab_glp_hero_cta',
        }}
        marqueeSignals={GLP_MARQUEE_SIGNALS}
        marqueeLabelPrefix={GLP_MARQUEE_PREFIX}
        marqueeAriaLabel={t('glp.marquee.aria')}
      />
      <TextRevealSection
        ariaLabel={t('glp.intro.aria')}
        bodyText={t('glp.intro.body')}
        backgroundColor="#ededed"
        textColor="#000"
        textMaxWidth="800px"
        scrollTrackVhMobile={2}
        scrollTrackVhDesktop={2}
      />
      <GlpLongevityProtocol />
      <GlpGuidedProtocol />
      <GlpKnowYourProgram />
      <GlpJourneyQuestions />
      <GlpScienceCards />
      <FaqSection
        faqData={glpFaqData}
        heading={t('glp.faq.heading')}
        ariaLabel={t('glp.faq.aria')}
        bgColor="grey"
      />
      <PincodeCtaBannerSection
        locationLabel={t('glp.pincode.location')}
        ctaLabel={t('glp.pincode.cta')}
        ctaAriaLabel={t('glp.pincode.ctaAria')}
        onCtaClick={handleGlpSignUp}
      />
      <GetDirectionsSection
        weekdayHours={t('glp.directions.weekdayHours')}
        sundayHours={t('glp.directions.sundayHours')}
      />
    </>
  );
};

export default PerformanceLabGlpPage;
